export function trim(text: string) {
  return text && text.replace(/^\s+|\s+$/g, '');
}

/**
 * Returns the argument if it is a string, or null otherwise.
 */
export function ifString(value: unknown) {
  return typeof value === 'string' ? value : null;
}

/**
 * Trim `text` to the given `length`, appending `append` to the end if `text` was trimmed.
 */
export function getTrimmedText(text: string | undefined, length: number, append = '…') {
  if (!text) {
    return '';
  }

  if (text.length <= length) {
    return text;
  }

  // Remove whitespace at end of trimmed string so there is no space between the text and the
  // ellipsis.
  const trimmedText = text.substring(0, length).replace(/\s+$/, '');

  return `${trimmedText}${append || ''}`;
}

/**
 * Returns the number of times the `substring` appears in `text`.
 */
export function findNumOccurrences(text: string, substring: string) {
  if (!substring || substring.length === 0) {
    throw new Error('findNumOccurrences got an empty substring.');
  }

  const string = text || '';
  const matches = string.match(makeEscapedRegExp(substring, 'g')) || [];

  return matches.length;
}

export function replaceAllOccurrences(text: string, substring: string, replacement: string) {
  return (text || '').replace(makeEscapedRegExp(substring, 'g'), replacement);
}

/**
 * Takes a string and returns a regex where all of the special characters in the string are escaped.
 *
 * Based on escapeRegExp from
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#Using_special_characters
 */
export function makeEscapedRegExp(text: string, flags: RegExp['flags']) {
  return new RegExp(
    escapeStringForRegExp(text),
    flags,
  );
}

export function escapeStringForRegExp(text: string) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

// We can't quote these identifiers because it will look in the system
// for fonts that are exactly that name, instead of generics
const unquotedFontIdentifiers = new Set([
  'serif',
  'sans-serif',
  'monospace',
  'cursive',
  'fantasy',
  'system-ui',
  'inherit',
  'initial',
  'unset',
]);

export const fontList = (fonts: string[]) => fonts.map(x => (
  unquotedFontIdentifiers.has(x) ? x : `'${x}'`
)).join(', ');
